import { format } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";

import { colors } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { DataGridPro, GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { PortfolioAnalyticsService } from "@analytics/index";

import RowActionsMenu from "@components/RowActionsMenu";
import ColumnHeader from "@components/Table/ColumnHeader";
import TableEmptyState from "@components/Table/TableEmptyState";

import useMenuState from "@hooks/useMenuState";
import useNavigateWithSearchParameters from "@hooks/useNavigateWithSearchParameters";

import { IAssignee, ListTechnologiesType, ListTechnologyType } from "@interfaces/portfolio.type";

import { useOrganizationTechnologiesQuery, useSetTechnologyAssignees } from "@queries/organization.query";
import { IArchivePortfolioMutationType, useArchiveTechnologyQuery, usePortfolioQuery } from "@queries/portfolio.query";

import { useOrganizationStore } from "@store/organizationStore";
import { PopupActions } from "@store/popupStore";

import contentCardStyles from "@theme/contentCardStyles";

import { getRowClassName, tableStyles } from "../Table/tableStyles";

import AssigneeSelectCellDropdown from "./components/AssigneeSelectCellDropdown";

const handleArchiveTechnology = (
  technologyId: string,
  technologyName: string,
  archiveTechnology: IArchivePortfolioMutationType,
) => {
  PopupActions.openConfirmation({
    content: (
      <Typography sx={{ mt: 1 }}>
        Are you sure you want to delete <strong>{technologyName}</strong>
        &nbsp; and its contents? You{" "}
        <strong>
          <u>cannot</u>
        </strong>{" "}
        &nbsp; undo this action.
      </Typography>
    ),
    onConfirm: () => {
      void archiveTechnology({ technologyId, technologyName });
    },
    title: "Delete Technology",
  });
};

interface IPortfolioTableProperties {
  isOrganization: boolean;
  organizationId?: string;
  searchQuery: string; // Accept searchQuery prop
  selectedAssignees?: string[];
}

export default function PortfolioTable({
  isOrganization,
  organizationId,
  searchQuery,
  selectedAssignees,
}: IPortfolioTableProperties) {
  const navigate = useNavigateWithSearchParameters();
  const menuState = useMenuState();

  const organizationQuery = useOrganizationTechnologiesQuery(organizationId);
  const portfolioQuery = usePortfolioQuery();

  const { data, status: portfolioLoadStatus } = isOrganization && organizationId ? organizationQuery : portfolioQuery;

  const { mutateAsync: archiveTechnology } = useArchiveTechnologyQuery();

  const { mutateAsync: setTechnologyAssignees } = useSetTechnologyAssignees();

  // If the organization query is being used, destructure portfolio from the response data

  // Use useMemo to memoize the portfolio value
  const portfolio: ListTechnologyType[] = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }
    return data?.portfolio || [];
  }, [data]);

  const { actions: OrganizationActions } = useOrganizationStore();

  const organizationUsers = OrganizationActions.getOrganizationUsers();

  useEffect(() => {
    if (portfolioLoadStatus === "error") {
      PopupActions.openNotification({
        content: "There was an issue loading your Portfolio. Please try again.",
        title: "Error",
      });
    }
  }, [portfolioLoadStatus]);

  const columns = useMemo<GridColDef<ListTechnologyType>[]>(() => {
    return [
      {
        field: "menuIdentifier",
        flexGrow: 0.1,

        renderCell: (parameters: GridRenderCellParams<ListTechnologyType>) => {
          const portfolioTableRowMenuItems = [
            {
              func: () => {
                menuState.handleClose();
                void handleArchiveTechnology(parameters.row.id, parameters.row.name, archiveTechnology);
              },
              label: "Delete Technology",
            },
          ];

          return <RowActionsMenu rowMenuItems={portfolioTableRowMenuItems} />;
        },
        renderHeader: () => <ColumnHeader title="" tooltip="" />,
      },

      {
        field: "name",
        flex: 2,
        renderCell: (parameters: GridRenderCellParams) => (
          <Typography
            sx={{
              color: colors.blue[600],
              cursor: "pointer",
              fontWeight: 600,
              textDecoration: "underline",
            }}
            variant="subtitle2"
          >
            {parameters.value}
          </Typography>
        ),
        renderHeader: () => <ColumnHeader title="Technology Name" tooltip="" />,
      },
      {
        field: "description", // Add description column
        flex: 2,
        hide: true,
        renderCell: (parameters: GridRenderCellParams) => (
          <Typography variant="subtitle2">{parameters.value}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Description" tooltip="" />,
      },
      {
        field: "numCompanies",
        flex: 1,
        renderHeader: () => (
          <ColumnHeader title="# Companies" tooltip="The number of companies saved to this Technology." />
        ),
        valueGetter: (value: string) => Number.parseInt(value, 10),
      },
      {
        field: "numContacts",
        flex: 1,
        renderHeader: () => (
          <ColumnHeader title="# Contacts" tooltip="The number of contacts saved to this Technology." />
        ),
        valueGetter: (value: string) => Number.parseInt(value, 10),
      },
      {
        field: "numCampaigns",
        flex: 1,
        renderHeader: () => <ColumnHeader title="# Campaigns" tooltip="The number of campaigns in this Technology." />,
        valueGetter: (value: string) => Number.parseInt(value, 10) || "0",
      },
      // Conditionally add the Assignee column here, after numContacts
      ...(isOrganization && organizationId
        ? [
            {
              cellClassName: "assignees-column",
              field: "assignee",
              flex: 2,
              renderCell: (parameters: GridRenderCellParams<ListTechnologyType>) => (
                <AssigneeSelectCellDropdown
                  organizationId={organizationId}
                  organizationUsers={organizationUsers}
                  row={parameters.row}
                  setTechnologyAssignees={(data) => {
                    void setTechnologyAssignees(data);
                  }}
                />
              ),
              renderHeader: () => (
                <ColumnHeader title="Assignee(s)" tooltip="The person responsible for this technology." />
              ),
              selectable: false,
            },
          ]
        : []),

      {
        field: "updated",
        flex: 1,

        renderCell: (parameters: GridRenderCellParams<ListTechnologyType>) => (
          <Typography variant="subtitle2">
            <Typography variant="subtitle2">{format(new Date(parameters.row.updated), "MM/dd/yy")}</Typography>
          </Typography>
        ),
        renderHeader: () => <ColumnHeader title="Updated" tooltip="" />,
      },
      {
        field: "created",
        flex: 1,
        renderCell: (parameters: GridRenderCellParams<ListTechnologyType>) => (
          <Typography variant="subtitle2">{format(new Date(parameters.row.created), "MM/dd/yy")}</Typography>
        ),
        renderHeader: () => <ColumnHeader title="Created" tooltip="" />,
        valueGetter: (value: string) => new Date(value),
      },
    ];
  }, [menuState, archiveTechnology]);

  const filteredRows = useMemo(() => {
    if (!portfolio) return [];
    return portfolio
      .map((tech) => ({
        ...tech,
        id: tech.id.toString(), // Convert the id to a string here
      }))
      .filter((tech) => {
        const matchesSearchQuery =
          tech.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          tech.description?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesAssigneeFilter =
          selectedAssignees?.length === 0 || // No assignee filter applied
          (tech.assignees?.some((assignee: IAssignee) => selectedAssignees?.includes(assignee.userAccountId)) ?? false);

        // Combine filters: Both must be true
        return matchesSearchQuery && matchesAssigneeFilter;
      });
  }, [portfolio, searchQuery, selectedAssignees]);

  const handleClick = useCallback(
    (cellParameters: GridCellParams<ListTechnologiesType>) => {
      if (["name"].includes(cellParameters.field)) {
        navigate(`/portfolio/technology/${cellParameters.id}/overview`);
      }
    },
    [navigate],
  );

  if (portfolioLoadStatus !== "success") {
    return <LinearProgress />;
  }

  let content;

  if (filteredRows.length === 0 && portfolioLoadStatus === "success") {
    content = (
      <TableEmptyState
        handleButtonClick={() => {
          /* Do Nothing */
        }}
        heading="Start Building Your Portfolio"
        resourceLink=""
        state={portfolioLoadStatus}
        supportingText="Explore and add companies or contacts to your Portfolio"
      />
    );
  } else {
    content = (
      <DataGridPro
        columns={columns.filter((column) => column.field !== "description")}
        disableColumnMenu={true}
        getRowClassName={getRowClassName}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 50 } },
          sorting: { sortModel: [{ field: "updated", sort: "desc" }] },
        }}
        localeText={{
          footerTotalRows: "Portfolio size:",
        }}
        onCellClick={(cellParameters: GridCellParams<ListTechnologiesType>) => handleClick(cellParameters)}
        pagination
        rows={filteredRows}
        scrollbarSize={50}
        sortingOrder={["desc", "asc"]}
        sx={{
          ...tableStyles.root,
          ...tableStyles.stretchColumns,
          ...contentCardStyles,
        }}
      />
    );
  }

  return content;
}
